import {
  Wifi, ShieldCheck, DatabaseBackup, Mail, Share2, MailWarning,
  KeyRound, CalendarDays, FileText, GraduationCap, Lock, Check,
} from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";

// Chiffres-clés mis en avant (tous issus du brief — rien d'inventé).
const STATS = [
  { value: "100 Go", label: "de boîte mail, par personne" },
  { value: "1 To", label: "d'espace partagé, par équipe" },
  { value: "24/7", label: "de surveillance humaine" },
  { value: "2 clés", label: "de sécurité, par personne" },
  { value: "2 × 10 min", label: "de formation, chaque mois" },
];

// Connectivité & périmètre — les fondations de l'entreprise.
const FOUNDATIONS = [
  {
    icon: Wifi,
    title: "Internet, avec une roue de secours",
    text: "La fibre Orange, l'un des réseaux les plus fiables de France, alimente toute votre entreprise. Et si elle venait à tomber, une bascule automatique en 4G prend le relais — le temps que la fibre revienne, votre activité continue de tourner et vos clients continuent de vous joindre.",
    chip: "Fibre Orange + secours 4G",
  },
  {
    icon: ShieldCheck,
    title: "Un poste de garde à l'entrée de votre réseau",
    text: "Le pare-feu d'entreprise contrôle la porte d'entrée de votre réseau. Nous l'installons, le configurons et le surveillons : il inspecte ce qui circule et écarte ce qui n'a rien à faire chez vous. Une première ligne de défense, invisible mais permanente.",
    chip: "Pare-feu installé & surveillé",
  },
];

// « Toujours une copie d'avance » — données & résilience (bloc à icônes, sans photo).
const RESILIENCE = [
  {
    icon: DatabaseBackup,
    title: "Sauvegarde complète, postes et serveurs",
    text: "En cas d'incident, de vol ou d'une simple erreur de manipulation, on restaure vos données à partir de la dernière sauvegarde. C'est le filet de sécurité que beaucoup d'entreprises croient avoir… jusqu'au jour où elles en ont besoin.",
  },
  {
    icon: Mail,
    title: "Messagerie protégée et sauvegardée",
    text: "La grande majorité des spams, arnaques et pièces jointes piégées sont filtrés en amont, avant d'atteindre les boîtes. Et vos messages sont sauvegardés — même celui qu'un salarié supprime par erreur un vendredi soir.",
  },
  {
    icon: Share2,
    title: "1 To d'espace partagé par équipe",
    text: "Un coffre commun pour travailler à plusieurs sur les mêmes documents, sans clés USB qui se perdent ni pièces jointes en dix versions. Chacun retrouve le bon fichier, au bon endroit.",
    chip: "1 To partagé",
  },
];

// Outils individuels — côté utilisateur.
const USER_TOOLS = [
  {
    icon: Mail,
    title: "Une messagerie spacieuse",
    text: "100 Go rien que pour vous, accessibles du bureau, de la maison ou du mobile. Vous ne triez plus vos mails par manque de place.",
    chip: "100 Go",
  },
  {
    icon: CalendarDays,
    title: "Un agenda partagé",
    text: "Réunions, disponibilités, planning d'équipe : tout le monde voit la même chose, sans allers-retours interminables.",
  },
  {
    icon: FileText,
    title: "La suite bureautique complète",
    text: "Documents, tableurs, visioconférence et messagerie interne. De quoi tout faire, au bureau comme en déplacement.",
  },
  {
    icon: KeyRound,
    title: "Un coffre-fort de mots de passe",
    text: "Sur ordinateur et sur mobile. Vous ne retenez plus qu'un seul mot de passe : il génère et retient tous les autres, uniques et complexes.",
  },
  {
    icon: GraduationCap,
    title: "Des réflexes en 10 minutes",
    text: "Deux micro-formations par mois, courtes et concrètes. Vos équipes montent en compétence sans y passer des heures.",
    chip: "2 × 10 min / mois",
  },
];

// Petit composant : puce de sous-point d'une ligne « marquee ».
const Point = ({ children }) => (
  <li className="flex items-start gap-3">
    <Check className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
    <span className="text-slate-300 leading-relaxed">{children}</span>
  </li>
);

export default function CeQueVousObtenez() {
  const { openAudit } = useAudit();
  return (
    <>
      <Seo
        title="Ce que vous obtenez concrètement"
        description="Concrètement, ce que votre entreprise obtient : connexion fibre + secours 4G, pare-feu, surveillance 24/7, protection des postes, sauvegarde, messagerie 100 Go, 1 To partagé, gestionnaire de mots de passe, clés de sécurité et formation des équipes."
        path="/ce-que-vous-obtenez"
        breadcrumb={[
          { name: "Accueil", path: "/" },
          { name: "Ce que vous obtenez", path: "/ce-que-vous-obtenez" },
        ]}
      />

      {/* HERO */}
      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-[-8%] w-[42rem] h-[42rem] rounded-full bg-amber-500/10 blur-[150px]" aria-hidden />
        <div className="pointer-events-none absolute bottom-0 left-[-10%] w-[34rem] h-[34rem] rounded-full bg-[#0b264e]/60 blur-[130px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Ce que vous obtenez</span></Reveal>
            <Reveal delay={0.05}>
              <h1 className="mt-5 font-display text-5xl sm:text-6xl md:text-7xl font-bold tracking-tighter text-white text-balance">
                Concrètement, voilà ce que vous obtenez.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-xl leading-relaxed">
                Oubliez les sigles techniques. Voici la photo précise de votre entreprise
                une fois équipée et protégée par nos soins — ce que gagne la société,
                et ce que vit chaque collaborateur, au quotidien.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-2xl">
              <img src="/photos/postes-securises.webp" width={1600} height={901} alt="Un poste de travail affichant un statut « sécurisé »" className="w-full h-[22rem] md:h-[26rem] object-cover" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRO — storytelling « un lundi matin » */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Un lundi matin comme les autres</span></Reveal>
          <Reveal delay={0.05}>
            <div className="mt-6 space-y-6 text-lg text-slate-600 leading-relaxed">
              <p>
                Il est 8h30. Vos équipes arrivent, allument leurs postes, ouvrent leur
                messagerie. Tout démarre, sans accroc. Personne ne pense à la sécurité
                informatique — et c'est précisément le signe que tout fonctionne.
              </p>
              <p>
                Derrière ce calme, une dizaine de protections travaillent en silence :
                une connexion doublée, un pare-feu qui filtre, une équipe qui veille, des
                sauvegardes qui tournent. Vous ne les voyez pas. Vous en mesurez la valeur
                le jour où un incident met une entreprise comme la vôtre à l'arrêt — et où
                vous, vous repartez en quelques heures.
              </p>
              <p className="text-navy font-medium">
                Voici, pièce par pièce, ce qui compose cette tranquillité.
              </p>
            </div>
          </Reveal>
        </div>
        {/* Chiffres-clés */}
        <div className="mx-auto max-w-5xl px-5 sm:px-8 mt-16">
          <Stagger className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {STATS.map((s) => (
              <StaggerItem key={s.value} className="rounded-xl border border-slate-200 bg-[#f8fafc] p-5 text-center">
                <p className="font-display text-2xl md:text-3xl font-bold text-navy tracking-tight">{s.value}</p>
                <p className="mt-1.5 text-xs text-slate-500 leading-snug">{s.label}</p>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ENTREPRISE — en-tête + fondations */}
      <section className="bg-[#f8fafc] py-24 md:py-28 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Côté entreprise</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Le socle, partagé par toute la société.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-lg text-slate-600 leading-relaxed">Ce sont les fondations : l'infrastructure et les services qui protègent l'ensemble de votre activité. Vous n'avez pas à vous en occuper — nous les installons, les configurons et les surveillons pour vous.</p></Reveal>
          </div>
          <Stagger className="grid md:grid-cols-2 gap-6">
            {FOUNDATIONS.map((f) => (
              <StaggerItem key={f.title} className="bg-white border border-slate-200 rounded-2xl p-8">
                <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center mb-5"><f.icon className="w-6 h-6 text-amber-500" /></div>
                <h3 className="font-display text-xl font-semibold tracking-tight text-navy">{f.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.text}</p>
                <span className="mt-5 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1">{f.chip}</span>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MARQUEE 1 — sentinelle 24/7 (photo datacenter) */}
      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-16 left-[-8%] w-[38rem] h-[38rem] rounded-full bg-amber-500/10 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Protection &amp; surveillance</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white text-balance">Une sentinelle sur chaque machine, jour et nuit.</h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">
              Sur chaque ordinateur et chaque serveur veille une protection de dernière
              génération. Au moindre comportement suspect — un fichier qui se met à
              chiffrer vos documents, un programme qui s'active tout seul — elle réagit en
              quelques secondes pour l'isoler et limiter sa propagation.
            </p>
            <ul className="mt-7 space-y-4">
              <Point>Un garde du corps sur chaque poste : les comportements suspects repérés et neutralisés automatiquement.</Point>
              <Point>Une équipe d'experts qui surveille vos alertes 24h/24 et 7j/7, même la nuit et le week-end.</Point>
              <Point>Seuls les logiciels que vous utilisez peuvent s'exécuter : tout le reste est bloqué par défaut, si bien qu'un rançongiciel inconnu n'a tout simplement pas la permission de démarrer.</Point>
            </ul>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="overflow-hidden rounded-2xl ring-1 ring-white/10 shadow-2xl">
              <img src="/photos/supervision-datacenter.webp" width={1400} height={935} alt="Supervision d'une salle de serveurs" className="w-full h-[26rem] object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE 2 — données & résilience (icônes) */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Vos données</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Toujours une copie d'avance.</h2></Reveal>
          </div>
          <Stagger className="grid md:grid-cols-3 gap-6">
            {RESILIENCE.map((r) => (
              <StaggerItem key={r.title} className="bg-[#f8fafc] border border-slate-200 rounded-2xl p-8">
                <div className="w-12 h-12 rounded-lg bg-navy grid place-items-center mb-5"><r.icon className="w-6 h-6 text-amber-500" /></div>
                <h3 className="font-display text-lg font-semibold tracking-tight text-navy leading-snug">{r.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{r.text}</p>
                {r.chip && <span className="mt-5 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1">{r.chip}</span>}
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MARQUEE 3 — tests de phishing (photo analyse) */}
      <section className="bg-[#f8fafc] py-24 md:py-28 border-y border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-xl">
              <img src="/photos/analyse-rapports.webp" width={1000} height={1778} alt="Analyse de rapports lors d'un point d'équipe" className="w-full h-[26rem] object-cover object-top" loading="lazy" />
            </div>
          </Reveal>
          <Reveal delay={0.12} className="order-1 lg:order-2">
            <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Le facteur humain</span>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-navy text-balance">Le piège testé avant que les pirates ne le tendent.</h2>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              La plupart des attaques commencent par un simple email. Alors nous prenons
              les devants : nous envoyons à vos équipes de fausses tentatives d'arnaque,
              grandeur nature. Personne ne risque rien, mais chacun apprend à reconnaître
              le vrai du faux.
            </p>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Vous obtenez un état clair des réflexes de votre équipe — et ils progressent,
              mois après mois. Le maillon le plus fragile devient l'un de vos meilleurs
              remparts.
            </p>
          </Reveal>
        </div>
      </section>

      {/* UTILISATEUR — clés (photo) + grille d'outils */}
      <section className="bg-white py-24 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Côté utilisateur</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-navy text-balance">Au quotidien, entre les mains de chacun.</h2></Reveal>
            <Reveal delay={0.1}><p className="mt-5 text-lg text-slate-600 leading-relaxed">Ce sont les outils que chaque collaborateur a rien qu'à lui. Pensés pour être simples : plus de sécurité, moins de friction.</p></Reveal>
          </div>

          {/* Feature clés de sécurité */}
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Deux clés de sécurité par personne</span>
              <h3 className="mt-4 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy text-balance">Deux clés, et l'esprit tranquille.</h3>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                Chaque personne reçoit deux clés de sécurité physiques. Pour se connecter,
                un simple geste : on insère la clé, et c'est vous. Résultat : un mot de
                passe volé ne suffit plus à entrer à votre place. La seconde clé est votre
                secours : en cas de perte, elle prend le relais, sans blocage.
              </p>
              <div className="mt-6 flex items-center gap-3 text-navy">
                <Lock className="w-5 h-5 text-amber-600" />
                <span className="font-medium">Offertes, et vous en restez propriétaire.</span>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xl">
                <img src="/photos/cle-securite.webp" width={1000} height={1500} alt="Une main tenant une clé de sécurité physique" className="w-full h-[26rem] object-cover" loading="lazy" />
              </div>
            </Reveal>
          </div>

          {/* Grille outils utilisateur */}
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {USER_TOOLS.map((t) => (
              <StaggerItem key={t.title} className="border border-slate-200 rounded-2xl p-7 hover:border-navy/30 hover:shadow-lg transition-[border-color,box-shadow] duration-300">
                <div className="w-11 h-11 rounded-lg bg-navy grid place-items-center mb-5"><t.icon className="w-5 h-5 text-amber-500" /></div>
                <h4 className="font-display text-lg font-semibold tracking-tight text-navy leading-snug">{t.title}</h4>
                <p className="mt-2.5 text-slate-600 leading-relaxed">{t.text}</p>
                {t.chip && <span className="mt-4 inline-block rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold px-3 py-1">{t.chip}</span>}
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* PROJECTION — le jour où ça compte */}
      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[54rem] h-[30rem] rounded-full bg-amber-500/12 blur-[140px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Reveal><span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-500">Le jour où ça compte</span></Reveal>
            <Reveal delay={0.05}><h2 className="mt-5 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">Le vrai test, c'est le jour où ça tourne mal.</h2></Reveal>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8">
                <div className="flex items-center gap-3 mb-4"><MailWarning className="w-5 h-5 text-slate-400" /><span className="text-sm uppercase tracking-widest font-medium text-slate-400">Sans tout ça</span></div>
                <p className="text-slate-300 leading-relaxed">
                  Un email piégé ouvert un mardi. Le lendemain, les fichiers sont chiffrés,
                  la compta à l'arrêt, personne ne sait par où commencer. Chaque heure sans
                  système coûte — et la confiance des clients, elle, ne se restaure pas d'un
                  clic.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-8">
                <div className="flex items-center gap-3 mb-4"><ShieldCheck className="w-5 h-5 text-amber-500" /><span className="text-sm uppercase tracking-widest font-medium text-amber-500">Avec Umbo</span></div>
                <p className="text-slate-200 leading-relaxed">
                  Le même email arrive. Il est filtré avant d'atteindre la boîte. Et si
                  quelque chose passait malgré tout, la menace est isolée avant de se
                  propager, vos données restent sauvegardées, et une équipe est déjà au
                  courant. Vous, vous n'avez rien vu.
                </p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.12}>
            <p className="mt-10 text-center font-display text-xl text-white/90">C'est toute la différence entre <span className="text-amber-500">subir</span> et <span className="text-amber-500">repartir vite</span>.</p>
          </Reveal>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="bg-navy-dark grain relative py-24 md:py-28 overflow-hidden">
        <div className="pointer-events-none absolute -bottom-16 right-[-6%] w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-[130px]" aria-hidden />
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">
              Alors, est-ce exactement ce qu'il vous faut ?
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed">
              Parlons-en simplement. On regarde ensemble votre installation actuelle et on
              vous dit, concrètement, ce que ça changerait pour vous.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="obtenez-cta">
              Réserver un échange gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
